import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import { supabase } from '@/lib/supabase';
import { isTelegramWebApp, readyTelegramWebApp, authenticateTelegramMiniApp } from '@/lib/telegram';
import { ensurePushRegistered } from '@/lib/push';
import { reportTimezone } from '@/lib/timezone';
import { installMfaResponseObserver, sessionNeedsCode } from '@/lib/mfa';
import type { User, Session } from '@supabase/supabase-js';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isTwa: boolean;
  twaAuthFailed: boolean;
  twaReason: 'not_registered' | 'error' | null;
  twaTelegram: string | null;
  /** Пароль (или Телеграм) принят, но у человека подключён код входа — ждём шесть цифр. */
  mfaPending: boolean;
  login: (email: string, password: string) => Promise<{ error: Error | null }>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isTwa] = useState<boolean>(() => isTelegramWebApp());
  const [twaAuthFailed, setTwaAuthFailed] = useState(false);
  // Почему не пустили. 'not_registered' — человека нет среди сотрудников;
  // это не поломка, и экран для него отдельный.
  const [twaReason, setTwaReason] = useState<'not_registered' | 'error' | null>(null);
  const [twaTelegram, setTwaTelegram] = useState<string | null>(null);
  const [mfaPending, setMfaPending] = useState(false);

  // Вход по коду (MFA_ENFORCEMENT_2026-10-24.md): сессия только по паролю у
  // человека с кодом — ещё не вход. Серверы её не примут (401 MFA_REQUIRED),
  // поэтому человек остаётся на экране входа и вводит код. Возвращает true,
  // если сессию можно пускать дальше.
  const applySession = (s: Session | null): boolean => {
    if (sessionNeedsCode(s)) {
      setMfaPending(true);
      setSession(null);
      setUser(null);
      return false;
    }
    setMfaPending(false);
    setSession(s);
    setUser(s?.user ?? null);
    return true;
  };

  useEffect(() => {
    const initAuth = async () => {
      try {
        // 1. Check for an existing local Supabase session first (fast path —
        // returning Telegram users keep their session across opens until refresh).
        const { data: { session: existingSession } } = await supabase.auth.getSession();
        if (existingSession) {
          applySession(existingSession);
          if (isTwa) readyTelegramWebApp();
          return;
        }

        // 2. Inside Telegram WebView: trade initData for a Supabase session.
        if (isTwa) {
          readyTelegramWebApp();
          const result = await authenticateTelegramMiniApp();
          if (!result.ok) {
            setTwaAuthFailed(true);
            setTwaReason(result.reason);
            setTwaTelegram(result.telegram ?? null);
          }
          // verifyOtp inside authenticateTelegramMiniApp also fires the
          // onAuthStateChange listener below, so state will be picked up
          // through that channel — nothing more to do here.
        }
      } catch (error) {
        console.error('[Auth] Failed to get session:', error);
      } finally {
        setIsLoading(false);
        // NOTE: __assistentusReady() is intentionally NOT called here.
        // Auth resolves before the chat session is ready; firing the
        // splash hand-off here used to expose a "third splash" — the
        // ChatPanel spinner — while the session bootstrapped. Each
        // top-level panel now fires __assistentusReady() once its own
        // data is loaded (see useBootReady hook).
      }
    };

    initAuth();

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, newSession) => {
        if (!applySession(newSession)) return;
        // Register the iOS APNs token as soon as we have a Supabase user.
        // ensurePushRegistered is idempotent + a no-op on web, so it's
        // safe to call on every auth change.
        if (newSession?.user?.id) {
          ensurePushRegistered(newSession.user.id).catch(() => {});
        }
      }
    );

    // Сервер ответил «нужен код входа» — показать шаг кода, а не выкидывать на вход.
    const removeMfaObserver = installMfaResponseObserver(() => {
      setMfaPending(true);
      setSession(null);
      setUser(null);
    });

    return () => { subscription.unsubscribe(); removeMfaObserver(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cold-start: if we already restored a session from local storage above,
  // the onAuthStateChange listener won't fire — kick off push registration
  // directly so the first app open registers a token without needing the
  // user to log in again.
  useEffect(() => {
    if (user?.id) ensurePushRegistered(user.id).catch(() => {});
    // Заодно сообщаем, в каком часовом поясе человек. Без этого напоминания
    // печатают время по Гринвичу — см. пояснение в lib/timezone.ts.
    if (user?.id) reportTimezone().catch(() => {});
  }, [user?.id]);

  const login = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) {
        return { error: error as Error };
      }
      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  };

  const logout = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
  };

  const value: AuthContextType = {
    user,
    session,
    isAuthenticated: !!session,
    isLoading,
    isTwa,
    twaAuthFailed,
    twaReason,
    twaTelegram,
    mfaPending,
    login,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
